<template>
  <div class="csv-diagnostics mt-4">
    <div v-if="loading" class="text-sm text-grey">Loading diagnostics...</div>
    <div v-else-if="!diagnostics" class="text-sm text-grey">
      No diagnostics available.
    </div>
    <div v-else>
      <div class="csv-diagnostics__summary">
        <span class="csv-diagnostics__badge" :class="statusClass">
          {{ statusLabel }}
        </span>
        <span class="ml-3"
          >Questions: {{ summary.amount_questions_config || 0 }}</span
        >
        <span class="ml-3">Pool: {{ summary.questions_pool_count || 0 }}</span>
        <span class="ml-3"
          >Objectives: {{ summary.objectives_config_count || 0 }}</span
        >
      </div>
      <div v-if="showCategoryDiagnostics" class="mt-2 flex flex-wrap">
        <vs-chip
          v-for="category in summary.categories_in_questions || []"
          :key="category"
          class="mr-2 mb-2"
          transparent
        >
          {{ category }}
        </vs-chip>
      </div>

      <vs-collapse type="border" class="mt-3">
        <vs-collapse-item-m v-if="hasStatusContent" open>
          <div slot="header" :class="sectionHeaderClass(statusSeverity)">
            Status
          </div>
          <div v-if="effectiveStatus.errors && effectiveStatus.errors.length">
            <div
              v-for="(error, index) in effectiveStatus.errors"
              :key="`error-${index}`"
              class="csv-diagnostics__error-block"
            >
              <div class="csv-diagnostics__error-title">
                {{ error.code }}: {{ error.message }}
              </div>
              <div v-if="error.details && error.details.length">
                <div
                  v-for="(detail, detailIndex) in error.details"
                  :key="`error-${index}-detail-${detailIndex}`"
                  class="csv-diagnostics__error-detail"
                >
                  <template v-if="error.code === 'CATEGORY_ORPHAN_IN_CONFIG'">
                    <div>Category: {{ detail.category }}</div>
                    <div>Rows: {{ formatRows(detail.row_numbers) }}</div>
                    <div v-if="detail.closest_match">
                      Closest match: {{ detail.closest_match }}
                    </div>
                    <div
                      v-if="
                        detail.known_categories &&
                        detail.known_categories.length
                      "
                      class="csv-diagnostics__known-categories"
                    >
                      <span class="mr-1">Known categories:</span>
                      <div class="csv-diagnostics__known-categories-chips">
                        <vs-chip
                          v-for="known in detail.known_categories.slice(0, 8)"
                          :key="`known-${known}`"
                          class="mr-1 mb-1"
                          transparent
                        >
                          {{ known }}
                        </vs-chip>
                      </div>
                    </div>
                  </template>
                  <template
                    v-else-if="error.code === 'CATEGORY_ORPHAN_IN_QUESTIONS'"
                  >
                    <div>Category: {{ detail.category }}</div>
                    <div>Rows: {{ formatRows(detail.row_numbers) }}</div>
                  </template>
                  <template v-else-if="error.code === 'OBJECTIVE_COVERAGE_GAP'">
                    <div>Objective: {{ detail.objective }}</div>
                    <div v-if="detail.objective_row">
                      Objective row: {{ detail.objective_row }}
                    </div>
                    <div>
                      Required vs available:
                      {{ detail.required || 0 }} / {{ detail.available || 0 }}
                    </div>
                  </template>
                  <template v-else-if="error.code === 'CATEGORY_COVERAGE_GAP'">
                    <div>Objective: {{ detail.objective }}</div>
                    <div v-if="detail.objective_row">
                      Objective row: {{ detail.objective_row }}
                    </div>
                    <div>Category: {{ detail.category }}</div>
                    <div v-if="detail.objective_category_row">
                      Objective category row:
                      {{ detail.objective_category_row }}
                    </div>
                    <div>
                      Required vs available:
                      {{ detail.required || 0 }} / {{ detail.available || 0 }}
                    </div>
                    <div
                      v-if="
                        detail.question_row_numbers_for_objective_category &&
                        detail.question_row_numbers_for_objective_category
                          .length
                      "
                    >
                      Question rows:
                      {{
                        formatRows(
                          detail.question_row_numbers_for_objective_category
                        )
                      }}
                    </div>
                  </template>
                  <template v-else>
                    <pre class="csv-diagnostics__json">{{ formatDiagnosticValue(detail) }}</pre>
                  </template>
                </div>
              </div>
              <div v-else class="text-sm text-grey">
                {{ error.message }}
              </div>
            </div>
          </div>
          <pre v-else class="csv-diagnostics__json">{{ formatDiagnosticValue(status) }}</pre>
        </vs-collapse-item-m>
        <vs-collapse-item-m v-if="hasSummaryContent">
          <div slot="header" :class="sectionHeaderClass(statusSeverity)">
            Summary
          </div>
          <pre class="csv-diagnostics__json">{{ formatDiagnosticValue(summary) }}</pre>
        </vs-collapse-item-m>
        <vs-collapse-item-m v-if="hasCategoryConsistencyContent">
          <div
            slot="header"
            :class="sectionHeaderClass(categoryConsistencySeverity)"
          >
            Category Consistency
          </div>
          <pre class="csv-diagnostics__json">{{ formatDiagnosticValue(categoryConsistency) }}</pre>
        </vs-collapse-item-m>
        <vs-collapse-item-m v-if="hasCoverageContent">
          <div slot="header" :class="sectionHeaderClass(coverageSeverity)">
            Coverage
          </div>
          <label class="text-sm">
            <input v-model="showAllRows" type="checkbox" class="mr-2" />
            Show all rows
          </label>
          <h5 class="mt-3 mb-2">objective_coverage</h5>
          <table class="csv-diagnostics__table">
            <tr>
              <th>Objective</th>
              <th>Required</th>
              <th>Available</th>
              <th>Gap</th>
            </tr>
            <tr
              v-for="row in filteredObjectiveCoverage"
              :key="`obj-${row.objective}`"
            >
              <td>{{ row.objective }}</td>
              <td>{{ (row.required && row.required.total) || 0 }}</td>
              <td>{{ (row.available && row.available.total) || 0 }}</td>
              <td>{{ row.has_gap ? 'Yes' : 'No' }}</td>
            </tr>
          </table>
          <h5 v-if="showCategoryDiagnostics" class="mt-3 mb-2">
            category_coverage_per_objective
          </h5>
          <table v-if="showCategoryDiagnostics" class="csv-diagnostics__table">
            <tr>
              <th>Objective</th>
              <th>Category</th>
              <th>Required</th>
              <th>Available</th>
              <th>Gap</th>
            </tr>
            <tr
              v-for="row in filteredCategoryCoverage"
              :key="`cat-${row.objective}-${row.category}`"
            >
              <td>{{ row.objective }}</td>
              <td>{{ row.category }}</td>
              <td>{{ row.required || 0 }}</td>
              <td>{{ row.available || 0 }}</td>
              <td>{{ row.has_gap ? 'Yes' : 'No' }}</td>
            </tr>
          </table>
        </vs-collapse-item-m>
        <vs-collapse-item-m v-if="hasStringQualityContent">
          <div slot="header" :class="sectionHeaderClass(stringQualitySeverity)">
            String Quality
          </div>
          <pre class="csv-diagnostics__json">{{ formatDiagnosticValue(stringQuality) }}</pre>
        </vs-collapse-item-m>
      </vs-collapse>
    </div>
  </div>
</template>

<script>
import { buildEffectiveDiagnosticsStatus } from '@/utils/csvDiagnostics'

export default {
  name: 'csv-diagnostics-accordion',
  props: {
    diagnostics: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showCategoryDiagnostics: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    showAllRows: false,
  }),
  computed: {
    status() {
      return (this.diagnostics && this.diagnostics.status) || {}
    },
    summary() {
      const summary = (this.diagnostics && this.diagnostics.summary) || {}
      if (this.showCategoryDiagnostics) return summary

      const {
        categories_in_questions: _ignoredCategoriesInQuestions,
        ...summaryWithoutCategories
      } = summary
      return summaryWithoutCategories
    },
    categoryConsistency() {
      return (this.diagnostics && this.diagnostics.category_consistency) || {}
    },
    coverage() {
      return (this.diagnostics && this.diagnostics.coverage) || {}
    },
    stringQuality() {
      return (this.diagnostics && this.diagnostics.string_quality) || {}
    },
    effectiveStatus() {
      return buildEffectiveDiagnosticsStatus(
        this.status,
        this.showCategoryDiagnostics
      )
    },
    statusLabel() {
      if (this.effectiveStatus.ok === false) return 'ERROR'
      if (
        this.effectiveStatus.ok === true &&
        (this.effectiveStatus.warnings || []).length > 0
      )
        return 'WARNING'
      return 'OK'
    },
    statusClass() {
      if (this.statusLabel === 'ERROR') return 'is-danger'
      if (this.statusLabel === 'WARNING') return 'is-warning'
      return 'is-success'
    },
    filteredObjectiveCoverage() {
      const rows = this.coverage.objective_coverage || []
      if (this.showAllRows) return rows
      return rows.filter(
        (row) => row.has_gap || (row.required && row.required.total > 0)
      )
    },
    filteredCategoryCoverage() {
      const rows = this.coverage.category_coverage_per_objective || []
      if (this.showAllRows) return rows
      return rows.filter((row) => row.has_gap || Number(row.required || 0) > 0)
    },
    statusSeverity() {
      if (
        this.effectiveStatus.ok === false ||
        (this.effectiveStatus.errors || []).length > 0
      )
        return 'danger'
      if ((this.effectiveStatus.warnings || []).length > 0) return 'warning'
      return 'none'
    },
    categoryConsistencySeverity() {
      if (!this.showCategoryDiagnostics) return 'none'
      const consistency = this.categoryConsistency || {}
      const hasMismatch =
        (consistency.label_mismatch_groups || []).length > 0 ||
        (consistency.orphans_in_questions || []).length > 0 ||
        (consistency.orphans_in_config || []).length > 0
      return hasMismatch ? 'warning' : 'none'
    },
    coverageSeverity() {
      const objectiveCoverage = this.coverage.objective_coverage || []
      const categoryCoverage = this.showCategoryDiagnostics
        ? this.coverage.category_coverage_per_objective || []
        : []
      const hasGap =
        objectiveCoverage.some((row) => Boolean(row && row.has_gap)) ||
        categoryCoverage.some((row) => Boolean(row && row.has_gap))
      return hasGap ? 'danger' : 'none'
    },
    stringQualitySeverity() {
      const entries = this.stringQuality.potential_mojibake_entries || []
      return entries.length > 0 ? 'warning' : 'none'
    },
    hasStatusContent() {
      return Object.keys(this.effectiveStatus || {}).length > 0
    },
    hasSummaryContent() {
      return Object.keys(this.summary || {}).length > 0
    },
    hasCategoryConsistencyContent() {
      if (!this.showCategoryDiagnostics) return false
      return Object.keys(this.categoryConsistency || {}).length > 0
    },
    hasCoverageContent() {
      const coverage = this.coverage || {}
      return (
        Object.keys(coverage).length > 0 &&
        ((coverage.objective_coverage &&
          coverage.objective_coverage.length > 0) ||
          (this.showCategoryDiagnostics &&
            coverage.category_coverage_per_objective &&
            coverage.category_coverage_per_objective.length > 0))
      )
    },
    hasStringQualityContent() {
      const entries =
        (this.stringQuality && this.stringQuality.potential_mojibake_entries) ||
        []
      return Array.isArray(entries) && entries.length > 0
    },
  },
  methods: {
    sectionHeaderClass(severity) {
      if (severity === 'danger') return 'csv-diagnostics__section--danger'
      if (severity === 'warning') return 'csv-diagnostics__section--warning'
      return 'csv-diagnostics__section--success'
    },
    formatRows(rows) {
      if (!Array.isArray(rows) || !rows.length) return '-'
      return rows.map((row) => `${row}`).join(', ')
    },
    formatDiagnosticValue(value) {
      if (value == null) return ''
      if (typeof value === 'string') return value

      try {
        return JSON.stringify(value, null, 2)
      } catch (error) {
        return String(value)
      }
    },
  },
}
</script>

<style scoped>
.csv-diagnostics__summary {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
}
.csv-diagnostics__badge {
  border-radius: 4px;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.45rem;
}
.is-success {
  background: #28c76f;
}
.is-warning {
  background: #ff9f43;
}
.is-danger {
  background: #ea5455;
}
.csv-diagnostics__json {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}
.csv-diagnostics__table {
  border-collapse: collapse;
  font-size: 12px;
  width: 100%;
}
.csv-diagnostics__table th,
.csv-diagnostics__table td {
  border: 1px solid #e5e7eb;
  padding: 4px 6px;
}
.csv-diagnostics__section--warning {
  background: #ff9f43;
  border-radius: 4px;
  color: #fff;
  display: inline-block;
  font-weight: 700;
  padding: 2px 8px;
}
.csv-diagnostics__section--danger {
  background: #ea5455;
  border-radius: 4px;
  color: #fff;
  display: inline-block;
  font-weight: 700;
  padding: 2px 8px;
}
.csv-diagnostics__section--success {
  background: #28c76f;
  border-radius: 4px;
  color: #fff;
  display: inline-block;
  font-weight: 700;
  padding: 2px 8px;
}
.csv-diagnostics__error-block {
  border: 1px solid #f0d0d0;
  border-radius: 6px;
  margin-bottom: 8px;
  padding: 8px;
}
.csv-diagnostics__error-title {
  font-weight: 700;
  margin-bottom: 6px;
}
.csv-diagnostics__error-detail {
  font-size: 12px;
  margin-bottom: 6px;
}
.csv-diagnostics__known-categories {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.csv-diagnostics__known-categories-chips {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
}
</style>
